import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createIndexedDBStorage } from '@creanizer/utils'

// フォルダの型定義をエクスポート
export interface Folder {
  id: string
  name: string
  parentId?: string // 階層構造用（将来的な拡張用）
  itemIds: string[] // アイテムのIDリスト
  color?: string // フォルダの色（任意）
  icon?: string // フォルダのアイコン（任意）
  createdAt: string
  updatedAt: string
  order: number // 表示順序
}

// フォルダ構造の型定義（ジェネレータごとに管理）
export interface FolderStructure {
  [generatorType: string]: {
    folders: { [folderId: string]: Folder }
    uncategorizedItemIds: string[] // フォルダに属さないアイテムのID
  }
}

interface FolderStore {
  // 全体の構造
  folderStructure: FolderStructure
  
  // フォルダ操作
  createFolder: (generatorType: string, name: string, parentId?: string) => string
  updateFolder: (generatorType: string, folderId: string, updates: Partial<Folder>) => void
  deleteFolder: (generatorType: string, folderId: string, moveItemsToUncategorized?: boolean) => void
  
  // アイテム操作
  addItemToFolder: (generatorType: string, itemId: string, folderId: string) => void
  removeItemFromFolder: (generatorType: string, itemId: string, folderId: string) => void
  moveItemBetweenFolders: (generatorType: string, itemId: string, fromFolderId: string | null, toFolderId: string | null) => void
  
  // 取得系
  getFolders: (generatorType: string) => Folder[]
  getFolderById: (generatorType: string, folderId: string) => Folder | undefined
  getItemsInFolder: (generatorType: string, folderId: string | null) => string[]
  getItemFolder: (generatorType: string, itemId: string) => Folder | null
  
  // 階層構造サポート
  getChildFolders: (generatorType: string, parentId: string | undefined) => Folder[]
  getFolderDepth: (generatorType: string, folderId: string) => number
  getFolderPath: (generatorType: string, folderId: string) => Folder[]
  canCreateSubfolder: (generatorType: string, parentId: string | undefined) => boolean
  
  // ユーティリティ
  reorderFolders: (generatorType: string, folderIds: string[]) => void
  cleanupOrphanedItems: (generatorType: string, validItemIds: string[]) => void
}

export const useFolderStore = create<FolderStore>()(
  persist(
    (set, get) => ({
      folderStructure: {},
      
      // フォルダを作成
      createFolder: (generatorType, name, parentId) => {
        const id = `folder_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        
        set(state => {
          const structure = { ...state.folderStructure }
          if (!structure[generatorType]) {
            structure[generatorType] = {
              folders: {},
              uncategorizedItemIds: []
            }
          }
          
          const newFolder: Folder = {
            id,
            name,
            parentId,
            itemIds: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            order: Object.keys(structure[generatorType].folders).length
          }
          
          structure[generatorType].folders[id] = newFolder
          return { folderStructure: structure }
        })
        
        return id
      },
      
      // フォルダを更新
      updateFolder: (generatorType, folderId, updates) => {
        set(state => {
          const structure = { ...state.folderStructure }
          if (structure[generatorType]?.folders[folderId]) {
            structure[generatorType].folders[folderId] = {
              ...structure[generatorType].folders[folderId],
              ...updates,
              updatedAt: new Date().toISOString()
            }
          }
          return { folderStructure: structure }
        })
      },
      
      // フォルダを削除（子フォルダも含む）
      deleteFolder: (generatorType, folderId, moveItemsToUncategorized = true) => {
        set(state => {
          const structure = { ...state.folderStructure }
          if (!structure[generatorType]) return state
          
          const folder = structure[generatorType].folders[folderId]
          if (!folder) return state
          
          // 子フォルダを再帰的に収集
          const collectChildFolderIds = (parentId: string, visited = new Set<string>()): string[] => {
            // 循環参照防止
            if (visited.has(parentId)) {
              console.error('Circular reference detected while collecting child folders')
              return []
            }
            visited.add(parentId)
            
            const childIds: string[] = []
            Object.values(structure[generatorType].folders).forEach(f => {
              if (f.parentId === parentId) {
                childIds.push(f.id)
                childIds.push(...collectChildFolderIds(f.id, visited))
              }
            })
            return childIds
          }
          
          const childFolderIds = collectChildFolderIds(folderId)
          const allFolderIds = [folderId, ...childFolderIds]
          
          // すべてのフォルダのアイテムを未分類に移動
          if (moveItemsToUncategorized) {
            allFolderIds.forEach(id => {
              const f = structure[generatorType].folders[id]
              if (f && f.itemIds.length > 0) {
                structure[generatorType].uncategorizedItemIds = [
                  ...structure[generatorType].uncategorizedItemIds,
                  ...f.itemIds
                ]
              }
            })
          }
          
          // すべてのフォルダを削除
          allFolderIds.forEach(id => {
            delete structure[generatorType].folders[id]
          })
          
          return { folderStructure: structure }
        })
      },
      
      // アイテムをフォルダに追加
      addItemToFolder: (generatorType, itemId, folderId) => {
        set(state => {
          const structure = { ...state.folderStructure }
          if (!structure[generatorType]) {
            structure[generatorType] = {
              folders: {},
              uncategorizedItemIds: []
            }
          }
          
          // まず既存のフォルダから削除
          Object.values(structure[generatorType].folders).forEach(folder => {
            folder.itemIds = folder.itemIds.filter(id => id !== itemId)
          })
          structure[generatorType].uncategorizedItemIds = 
            structure[generatorType].uncategorizedItemIds.filter(id => id !== itemId)
          
          // 新しいフォルダに追加
          if (structure[generatorType].folders[folderId]) {
            structure[generatorType].folders[folderId].itemIds.push(itemId)
            structure[generatorType].folders[folderId].updatedAt = new Date().toISOString()
          }
          
          return { folderStructure: structure }
        })
      },
      
      // アイテムをフォルダから削除
      removeItemFromFolder: (generatorType, itemId, folderId) => {
        set(state => {
          const structure = { ...state.folderStructure }
          if (!structure[generatorType]) return state
          
          if (structure[generatorType].folders[folderId]) {
            structure[generatorType].folders[folderId].itemIds = 
              structure[generatorType].folders[folderId].itemIds.filter(id => id !== itemId)
            structure[generatorType].folders[folderId].updatedAt = new Date().toISOString()
          }
          
          // 未分類に追加
          if (!structure[generatorType].uncategorizedItemIds.includes(itemId)) {
            structure[generatorType].uncategorizedItemIds.push(itemId)
          }
          
          return { folderStructure: structure }
        })
      },
      
      // アイテムをフォルダ間で移動
      moveItemBetweenFolders: (generatorType, itemId, fromFolderId, toFolderId) => {
        set(state => {
          const structure = { ...state.folderStructure }
          if (!structure[generatorType]) {
            structure[generatorType] = {
              folders: {},
              uncategorizedItemIds: []
            }
          }
          
          // 元のフォルダから削除
          if (fromFolderId && structure[generatorType].folders[fromFolderId]) {
            structure[generatorType].folders[fromFolderId].itemIds = 
              structure[generatorType].folders[fromFolderId].itemIds.filter(id => id !== itemId)
            structure[generatorType].folders[fromFolderId].updatedAt = new Date().toISOString()
          } else {
            // 未分類から削除
            structure[generatorType].uncategorizedItemIds = 
              structure[generatorType].uncategorizedItemIds.filter(id => id !== itemId)
          }
          
          // 新しいフォルダに追加
          if (toFolderId && structure[generatorType].folders[toFolderId]) {
            structure[generatorType].folders[toFolderId].itemIds.push(itemId)
            structure[generatorType].folders[toFolderId].updatedAt = new Date().toISOString()
          } else {
            // 未分類に追加
            if (!structure[generatorType].uncategorizedItemIds.includes(itemId)) {
              structure[generatorType].uncategorizedItemIds.push(itemId)
            }
          }
          
          return { folderStructure: structure }
        })
      },
      
      // フォルダ一覧を取得
      getFolders: (generatorType) => {
        const structure = get().folderStructure[generatorType]
        if (!structure) return []
        return Object.values(structure.folders).sort((a, b) => a.order - b.order)
      },
      
      // フォルダを取得
      getFolderById: (generatorType, folderId) => {
        return get().folderStructure[generatorType]?.folders[folderId]
      },
      
      // フォルダ内のアイテムIDを取得
      getItemsInFolder: (generatorType, folderId) => {
        const structure = get().folderStructure[generatorType]
        if (!structure) return []
        
        if (folderId === null) {
          // 未分類のアイテム
          return structure.uncategorizedItemIds || []
        }
        
        return structure.folders[folderId]?.itemIds || []
      },
      
      // アイテムが属するフォルダを取得
      getItemFolder: (generatorType, itemId) => {
        const structure = get().folderStructure[generatorType]
        if (!structure) return null
        
        for (const folder of Object.values(structure.folders)) {
          if (folder.itemIds.includes(itemId)) {
            return folder
          }
        }
        
        return null
      },
      
      // 子フォルダを取得
      getChildFolders: (generatorType, parentId) => {
        const structure = get().folderStructure[generatorType]
        if (!structure) return []
        return Object.values(structure.folders)
          .filter(folder => folder.parentId === parentId)
          .sort((a, b) => a.order - b.order)
      },
      
      // フォルダの階層の深さを取得（0がルート、最大2まで）
      getFolderDepth: (generatorType, folderId) => {
        const structure = get().folderStructure[generatorType]
        if (!structure) return 0
        
        let depth = 0
        let currentFolder = structure.folders[folderId]
        const visited = new Set<string>()
        
        while (currentFolder?.parentId && depth < 3) {
          // 循環参照防止
          if (visited.has(currentFolder.id)) {
            console.error('Circular reference detected in folder hierarchy')
            break
          }
          visited.add(currentFolder.id)
          
          depth++
          currentFolder = structure.folders[currentFolder.parentId]
        }
        
        return depth
      },
      
      // フォルダのパス（ルートから現在のフォルダまで）を取得
      getFolderPath: (generatorType, folderId) => {
        const structure = get().folderStructure[generatorType]
        if (!structure) return []
        
        const path: Folder[] = []
        let currentFolder = structure.folders[folderId]
        const visited = new Set<string>()
        
        while (currentFolder) {
          // 循環参照防止
          if (visited.has(currentFolder.id)) {
            console.error('Circular reference detected in folder path')
            break
          }
          visited.add(currentFolder.id)
          
          path.unshift(currentFolder)
          currentFolder = currentFolder.parentId ? structure.folders[currentFolder.parentId] : undefined
        }
        
        return path
      },
      
      // サブフォルダを作成可能かどうか（階層制限チェック）
      canCreateSubfolder: (generatorType, parentId) => {
        if (!parentId) return true // ルートレベルは常にOK
        
        const structure = get().folderStructure[generatorType]
        if (!structure) return false
        
        // 階層の深さを直接計算（getFolderDepthを呼び出さない）
        let depth = 0
        let currentFolder = structure.folders[parentId]
        const visited = new Set<string>()
        
        while (currentFolder?.parentId && depth < 3) {
          if (visited.has(currentFolder.id)) {
            console.error('Circular reference detected in canCreateSubfolder')
            break
          }
          visited.add(currentFolder.id)
          depth++
          currentFolder = structure.folders[currentFolder.parentId]
        }
        
        return depth < 2 // 親が階層2未満ならOK（0=ルート、1=階層1なら、階層2まで作成可能）
      },
      
      // フォルダの順序を変更
      reorderFolders: (generatorType, folderIds) => {
        set(state => {
          const structure = { ...state.folderStructure }
          if (!structure[generatorType]) return state
          
          folderIds.forEach((id, index) => {
            if (structure[generatorType].folders[id]) {
              structure[generatorType].folders[id].order = index
            }
          })
          
          return { folderStructure: structure }
        })
      },
      
      // 削除されたアイテムのIDをクリーンアップ
      cleanupOrphanedItems: (generatorType, validItemIds) => {
        set(state => {
          const structure = { ...state.folderStructure }
          if (!structure[generatorType]) return state
          
          // 各フォルダから無効なアイテムIDを削除
          Object.values(structure[generatorType].folders).forEach(folder => {
            folder.itemIds = folder.itemIds.filter(id => validItemIds.includes(id))
          })
          
          // 未分類からも削除
          structure[generatorType].uncategorizedItemIds = 
            structure[generatorType].uncategorizedItemIds.filter(id => validItemIds.includes(id))
          
          return { folderStructure: structure }
        })
      }
    }),
    {
      name: 'folder-storage',
      version: 1,
      storage: createIndexedDBStorage('folder-storage'),
      partialize: (state) => ({ folderStructure: state.folderStructure })
    }
  )
)